import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { SlackConnectResult as SlackConnectResultComponent } from '@/modules/integrations/components/SlackConnectResult';
import { useConnectSlack } from '@/modules/integrations/hooks/useConnectSlack';
import { useSlackCallbackParams } from '@/modules/integrations/hooks/useSlackCallbackParams';
import { Button } from '@/modules/ui/components/button/Button';

const statusCopy = {
  success: {
    title: 'Slack connected',
    description:
      'Your Slack workspace is now linked to Finlay. You can manage notifications and automations directly from your external accounts. You can close this tab and go back to Finlay.',
  },
  failure: {
    title: 'Slack connection failed',
    description:
      'We could not finish connecting Slack. Please try again or reach out to support if the issue persists.',
  },
  cancelled: {
    title: 'Slack isn’t connected',
    description:
      'You stopped before Slack finished connecting. Try again whenever you’re ready.',
  },
} as const;

interface SlackConnectResultPageProps {
  renderActions: (params: {
    appId: string | null;
    teamId: string | null;
    goBackButton: ReactNode;
  }) => ReactNode;
  goBackPath?: string;
}

export const SlackConnectResult = ({
  renderActions,
  goBackPath = '/jobs',
}: SlackConnectResultPageProps) => {
  const { status, appId, teamId, isCancelled } = useSlackCallbackParams();

  const connectSlack = useConnectSlack();

  const resultStatus = isCancelled ? 'cancelled' : status;
  const { title, description } = statusCopy[resultStatus];

  const goBackButton = (
    <Button variant="outline" asChild>
      <Link to={goBackPath}>Go back</Link>
    </Button>
  );

  const actions =
    status === 'success' ? (
      renderActions({ appId, teamId, goBackButton })
    ) : (
      <>
        <Button variant="primary" onClick={() => connectSlack('App')}>
          Try again
        </Button>
        {goBackButton}
      </>
    );

  return (
    <SlackConnectResultComponent
      title={title}
      description={description}
      status={resultStatus}
      actions={actions}
    />
  );
};
